// ShareMessageContext — shared across Alexandria and Yochai.
//
// Wraps the share_messages Supabase table behind a typed lookup. Each app mounts
// the provider once with its own SupabaseClient instance; the React Query cache
// holds the templates indefinitely (they rarely change).
//
// Template lookup is the same as before — getShareMessageTemplate(type) returns the
// raw template string with {{placeholder}} tokens that useShareHandler interpolates.

import React, { createContext, useContext, useEffect, useCallback, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';

interface ShareMessageContextType {
  messages: Record<string, string>;
  isLoading: boolean;
  getShareMessageTemplate: (type: string) => string | null;
}

const ShareMessageContext = createContext<ShareMessageContextType | undefined>(undefined);

const fetchShareMessages = async (supabase: SupabaseClient): Promise<Record<string, string>> => {
  const { data, error } = await supabase
    .from('share_messages')
    .select('type, message');

  if (error) {
    console.error('[ShareMessageContext] fetch failed:', error);
    throw new Error(error.message);
  }

  const map: Record<string, string> = {};
  (data ?? []).forEach((row: { type: string | null; message: string | null }) => {
    if (row.type && row.message) {
      map[row.type] = row.message;
    }
  });
  return map;
};

interface ShareMessageProviderProps {
  children: ReactNode;
  /**
   * The app's Supabase client. Each app passes its own (Alexandria's vs Yochai's),
   * which keeps the provider environment-agnostic.
   */
  supabase: SupabaseClient;
  /**
   * Optional query-key suffix to scope the cache per app when multiple providers
   * live in the same React tree (rare). Defaults to 'global'.
   */
  cacheScope?: string;
}

export function ShareMessageProvider({ children, supabase, cacheScope = 'global' }: ShareMessageProviderProps) {
  const { data: messages = {}, isLoading, error } = useQuery<Record<string, string>>({
    queryKey: ['shareMessages', cacheScope],
    queryFn: () => fetchShareMessages(supabase),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error) {
      console.error('[ShareMessageContext] error:', error);
    }
  }, [error]);

  const getShareMessageTemplate = useCallback(
    (type: string): string | null => messages[type] ?? null,
    [messages]
  );

  return (
    <ShareMessageContext.Provider value={{ messages, isLoading, getShareMessageTemplate }}>
      {children}
    </ShareMessageContext.Provider>
  );
}

export function useShareMessages(): ShareMessageContextType {
  const context = useContext(ShareMessageContext);
  if (context === undefined) {
    throw new Error('useShareMessages must be used within a ShareMessageProvider');
  }
  return context;
}
